import axios from 'axios';
import { publicUrl } from '../utils/publicUrl';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8090/api';

function isAuthPagePath() {
  const path = window.location.pathname;
  const loginPath = publicUrl('/login');
  const registerPath = publicUrl('/register');
  const forgotPath = publicUrl('/passwort-vergessen');
  const resetPath = publicUrl('/passwort-reset');
  return [loginPath, registerPath, forgotPath, resetPath].some((p) => path === p || path.startsWith(`${p}/`));
}

function isPublicAuthRequest(url = '') {
  return ['/auth/signin', '/auth/signup', '/auth/forgot-password', '/auth/reset-password']
    .some((segment) => url.includes(segment));
}

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    config.headers['x-access-token'] = token;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isPublicAuthRequest(error.config?.url || '')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!isAuthPagePath()) {
        window.location.href = publicUrl('/login');
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/signin', { username, password });
    const { accessToken, ...user } = response.data;
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(user));
    return response.data;
  },
  register: async (username, email, password) => {
    const response = await api.post('/auth/signup', { username, email, password });
    return response.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  getCurrentUser: () => JSON.parse(localStorage.getItem('user') || 'null'),
};

export const tippspielAdminAPI = {
  getSettings: () => api.get('/admin/settings'),
  getMfleagueProjects: () => api.get('/admin/mfleague/projects'),
  getProjectCompetitions: (projectId) => api.get(`/admin/mfleague/projects/${projectId}/competitions`),
  getProjectSeasons: (projectId) => api.get(`/admin/mfleague/projects/${projectId}/seasons`),
  saveMfleagueProject: (data) => api.post('/admin/settings/mfleague-project', data),
  getScoring: () => api.get('/admin/settings/scoring'),
  updateScoring: (data) => api.put('/admin/settings/scoring', data),
  updateVisibility: (data) => api.put('/admin/settings/visibility', data),
  updateDeadlines: (data) => api.put('/admin/settings/deadlines', data),
  syncProject: (data) => api.post('/admin/sync/project', data || {}),
  getSyncStatus: () => api.get('/admin/sync/status'),
  getSyncLogs: () => api.get('/admin/sync/logs'),
  recalculate: (data) => api.post('/admin/recalculate', data || {}),
  recalculateMatch: (matchId) => api.post(`/admin/recalculate/match/${matchId}`),
  listMatches: (params) => api.get('/admin/matches', { params }),
  getParticipants: () => api.get('/admin/participants'),
};

export const tippspielAPI = {
  getInfo: () => api.get('/tippspiel/info'),
  getOverview: () => api.get('/tippspiel/overview'),
  getMatches: (params) => api.get('/tippspiel/matches', { params }),
  submitTip: (data) => api.post('/tippspiel/tips', data),
  getMyTips: () => api.get('/tippspiel/my-tips'),
  getMyStats: () => api.get('/tippspiel/my-stats'),
  getLeaderboard: () => api.get('/tippspiel/leaderboard'),
};

export default api;
